import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import './Withdraw.css';

function Withdraw({ contract, userId, setError }) {
  const [availableBalance, setAvailableBalance] = useState('0');
  const [earnings, setEarnings] = useState(null);
  const [pendingIncomes, setPendingIncomes] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [txStatus, setTxStatus] = useState('');
  const [dataLoading, setDataLoading] = useState(true);

  const loadEarnings = async () => {
    if (!contract || userId === 0) {
      setDataLoading(false);
      return;
    }

    try {
      const [available, earn, pendingBinary, pendingReferral] = await Promise.all([
        contract.getAvailableBalance(userId),
        contract.getEarningsBreakdown(userId),
        contract.getTotalPendingBinaryIncome(userId),
        contract.getTotalPendingReferralIncome(userId)
      ]);

      setAvailableBalance(ethers.utils.formatEther(available));
      setEarnings({
        roiEarned: ethers.utils.formatEther(earn.roiEarned),
        binaryEarned: ethers.utils.formatEther(earn.binaryEarned),
        referralEarned: ethers.utils.formatEther(earn.referralEarned),
        totalEarned: ethers.utils.formatEther(earn.totalEarned),
        totalWithdrawn: ethers.utils.formatEther(earn.totalWithdrawn)
      });
      setPendingIncomes({
        binaryTotal: ethers.utils.formatEther(pendingBinary),
        referralTotal: ethers.utils.formatEther(pendingReferral)
      });
    } catch (err) {
      console.error('Error loading earnings:', err);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    loadEarnings();
    const interval = setInterval(loadEarnings, 15000);
    return () => clearInterval(interval);
  }, [contract, userId]);

  const calculateNetAmount = () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) return { net: '0', fee: '0' };
    const amount = parseFloat(withdrawAmount);
    const fee = amount * 0.10; // 2% platform + 8% admin
    const net = amount * 0.90;
    return { net: net.toFixed(2), fee: fee.toFixed(2) };
  };

  const handleWithdraw = async () => {
    if (!contract || !withdrawAmount) return;

    const amount = parseFloat(withdrawAmount);
    if (amount < 10) {
      setError('Minimum withdrawal is $10 USDT');
      return;
    }

    if (amount > parseFloat(availableBalance)) {
      setError('Amount exceeds available balance');
      return;
    }

    setLoading(true);
    setTxStatus('Processing withdrawal...');

    try {
      const amountWei = ethers.utils.parseEther(withdrawAmount);
      const tx = await contract.withdraw(amountWei);
      setTxStatus('Transaction submitted. Waiting for confirmation...');
      await tx.wait();
      setTxStatus('Withdrawal successful!');
      setWithdrawAmount('');
      await loadEarnings();
      setTimeout(() => setTxStatus(''), 5000);
    } catch (err) {
      console.error('Withdrawal error:', err);
      setError(err.reason || err.message || 'Withdrawal failed');
      setTxStatus('');
    } finally {
      setLoading(false);
    }
  };

  const setPercentage = (percent) => {
    const amount = (parseFloat(availableBalance) * percent / 100).toFixed(2);
    setWithdrawAmount(amount);
  };

  if (dataLoading) {
    return (
      <div className="withdraw loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (userId === 0) {
    return (
      <div className="withdraw not-registered">
        <div className="section-icon">💸</div>
        <p>Register to access withdrawals</p>
      </div>
    );
  }

  return (
    <div className="withdraw">
      <div className="section-header">
        <h3>
          <span className="section-icon">💸</span>
          Withdraw Funds
        </h3>
      </div>

      <div className="available-balance">
        <div className="balance-label">Available</div>
        <div className="balance-value">${parseFloat(availableBalance).toFixed(2)}</div>
      </div>

      <div className="form-group">
        <label>Amount (USDT)</label>
        <input
          type="number"
          placeholder="Min $10"
          value={withdrawAmount}
          onChange={(e) => setWithdrawAmount(e.target.value)}
          disabled={loading}
          step="0.01"
          min="10"
          max={availableBalance}
        />
      </div>

      <div className="percentage-buttons">
        <button onClick={() => setPercentage(25)} disabled={loading}>25%</button>
        <button onClick={() => setPercentage(50)} disabled={loading}>50%</button>
        <button onClick={() => setPercentage(75)} disabled={loading}>75%</button>
        <button onClick={() => setPercentage(100)} disabled={loading}>MAX</button>
      </div>

      {withdrawAmount && parseFloat(withdrawAmount) >= 10 && (
        <div className="withdrawal-summary">
          <div className="summary-row">
            <span>Amount</span>
            <span>${parseFloat(withdrawAmount).toFixed(2)}</span>
          </div>
          <div className="summary-row fee">
            <span>Fee (10%)</span>
            <span>-${calculateNetAmount().fee}</span>
          </div>
          <div className="summary-divider"></div>
          <div className="summary-row total">
            <span>You Receive</span>
            <span>${calculateNetAmount().net}</span>
          </div>
        </div>
      )}

      {txStatus && (
        <div className="tx-status">
          <div className="status-spinner"></div>
          {txStatus}
        </div>
      )}

      <button
        className="withdraw-btn"
        onClick={handleWithdraw}
        disabled={loading || !withdrawAmount || parseFloat(withdrawAmount) < 10 || parseFloat(withdrawAmount) > parseFloat(availableBalance)}
      >
        {loading ? 'Processing...' : 'Withdraw'}
      </button>

      <div className="earnings-mini">
        <div className="earning-row">
          <span>ROI</span>
          <span className="green">${parseFloat(earnings?.roiEarned || 0).toFixed(2)}</span>
        </div>
        <div className="earning-row">
          <span>Binary (Total)</span>
          <span className="white">${parseFloat(pendingIncomes?.binaryTotal || 0).toFixed(2)}</span>
        </div>
        <div className="earning-row">
          <span>Binary (Released)</span>
          <span className="blue">${parseFloat(earnings?.binaryEarned || 0).toFixed(2)}</span>
        </div>
        <div className="earning-row progress-row">
          <div className="mini-progress-bar">
            <div 
              className="mini-progress-fill binary"
              style={{ 
                width: `${pendingIncomes?.binaryTotal > 0 
                  ? Math.min((parseFloat(earnings?.binaryEarned || 0) / parseFloat(pendingIncomes?.binaryTotal)) * 100, 100) 
                  : 0}%` 
              }}
            ></div>
          </div>
          <span className="progress-percent">
            {pendingIncomes?.binaryTotal > 0 
              ? ((parseFloat(earnings?.binaryEarned || 0) / parseFloat(pendingIncomes?.binaryTotal)) * 100).toFixed(1)
              : 0}%
          </span>
        </div>
        <div className="earning-row">
          <span>Referral (Total)</span>
          <span className="white">${parseFloat(pendingIncomes?.referralTotal || 0).toFixed(2)}</span>
        </div>
        <div className="earning-row">
          <span>Referral (Released)</span>
          <span className="purple">${parseFloat(earnings?.referralEarned || 0).toFixed(2)}</span>
        </div>
        <div className="earning-row progress-row">
          <div className="mini-progress-bar">
            <div 
              className="mini-progress-fill referral"
              style={{ 
                width: `${pendingIncomes?.referralTotal > 0 
                  ? Math.min((parseFloat(earnings?.referralEarned || 0) / parseFloat(pendingIncomes?.referralTotal)) * 100, 100) 
                  : 0}%` 
              }}
            ></div>
          </div>
          <span className="progress-percent">
            {pendingIncomes?.referralTotal > 0 
              ? ((parseFloat(earnings?.referralEarned || 0) / parseFloat(pendingIncomes?.referralTotal)) * 100).toFixed(1)
              : 0}%
          </span>
        </div>
      </div>
    </div>
  );
}

export default Withdraw;